import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useI18n } from '../../i18n/LanguageContext';
import { useCookieConsent, CookiePrefs } from '../../site/CookieConsent';

type Cat = { key: 'necessary' | 'maps' | 'analytics'; name: string; desc: string; };

const TXT: Record<string, {
  title: string; intro: string; policy: string;
  cats: Cat[]; always: string; save: string; all: string; close: string;
}> = {
  de: {
    title: 'Cookie-Einstellungen',
    intro: 'Wählen Sie, welche Cookies Sie zulassen möchten. Technisch notwendige Cookies sind immer aktiv. Details in unserer',
    policy: 'Cookie-Richtlinie',
    always: 'Immer aktiv',
    save: 'Einstellungen speichern',
    all: 'Alle akzeptieren',
    close: 'Schließen',
    cats: [
      { key: 'necessary', name: 'Notwendig', desc: 'Für den Betrieb der Website erforderlich (z. B. Sprache, Cookie-Auswahl). Immer aktiv.' },
      { key: 'maps', name: 'Google Maps', desc: 'Lädt die eingebetteten Karten in den Standorten von Google.' },
      { key: 'analytics', name: 'Statistik', desc: 'Anonyme Nutzungsstatistiken zur Verbesserung der Website.' },
    ],
  },
  en: {
    title: 'Cookie preferences',
    intro: 'Choose which cookies you allow. Strictly necessary cookies are always on. Details in our',
    policy: 'Cookie Policy',
    always: 'Always on',
    save: 'Save preferences',
    all: 'Accept all',
    close: 'Close',
    cats: [
      { key: 'necessary', name: 'Necessary', desc: 'Required for the website to work (e.g. language, cookie choice). Always on.' },
      { key: 'maps', name: 'Google Maps', desc: 'Loads the embedded maps in the Locations section from Google.' },
      { key: 'analytics', name: 'Statistics', desc: 'Anonymous usage statistics to help us improve the website.' },
    ],
  },
  it: {
    title: 'Preferenze cookie',
    intro: 'Scegli quali cookie consentire. I cookie tecnici necessari sono sempre attivi. Dettagli nella nostra',
    policy: 'Cookie Policy',
    always: 'Sempre attivi',
    save: 'Salva preferenze',
    all: 'Accetta tutti',
    close: 'Chiudi',
    cats: [
      { key: 'necessary', name: 'Necessari', desc: 'Indispensabili per il funzionamento del sito (es. lingua, scelta cookie). Sempre attivi.' },
      { key: 'maps', name: 'Google Maps', desc: 'Carica le mappe incorporate nella sezione Sedi da Google.' },
      { key: 'analytics', name: 'Statistiche', desc: 'Statistiche di utilizzo anonime per migliorare il sito.' },
    ],
  },
};

const CookiePreferences: React.FC = () => {
  const { lang } = useI18n();
  const { prefsOpen, prefs, savePrefs, acceptAll, closePreferences } = useCookieConsent();
  const [local, setLocal] = useState<CookiePrefs>(prefs);
  const dialogRef = useRef<HTMLDivElement>(null);
  const c = TXT[lang] || TXT.de;

  // Reset the toggles to the saved prefs each time the modal opens, and focus it.
  useEffect(() => {
    if (prefsOpen) {
      setLocal(prefs);
      const id = window.setTimeout(() => dialogRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [prefsOpen, prefs]);

  // Esc closes; lock body scroll while open.
  useEffect(() => {
    if (!prefsOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePreferences(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [prefsOpen, closePreferences]);

  if (!prefsOpen) return null;

  const toggle = (key: 'maps' | 'analytics') => setLocal((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div className="cookie-modal-overlay" onClick={closePreferences}>
      <div
        className="cookie-modal"
        role="dialog"
        aria-modal="true"
        aria-label={c.title}
        tabIndex={-1}
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cookie-modal-head">
          <h3>{c.title}</h3>
          <button type="button" className="cookie-modal-close" onClick={closePreferences} aria-label={c.close}>
            <X size={20} aria-hidden />
          </button>
        </div>
        <p className="cookie-modal-intro">
          {c.intro} <Link to="/cookie-policy" onClick={closePreferences}>{c.policy}</Link>.
        </p>

        <ul className="cookie-cats">
          {c.cats.map((cat) => {
            const isNecessary = cat.key === 'necessary';
            const checked = isNecessary ? true : local[cat.key as 'maps' | 'analytics'];
            return (
              <li className="cookie-cat" key={cat.key}>
                <div className="cookie-cat-info">
                  <span className="cookie-cat-name">{cat.name}</span>
                  <span className="cookie-cat-desc">{cat.desc}</span>
                </div>
                {isNecessary ? (
                  <span className="cookie-always">{c.always}</span>
                ) : (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={checked}
                    aria-label={cat.name}
                    className={`cookie-toggle${checked ? ' on' : ''}`}
                    onClick={() => toggle(cat.key as 'maps' | 'analytics')}
                  >
                    <span className="cookie-toggle-knob" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <div className="cookie-modal-actions">
          <button type="button" className="cookie-btn ghost" onClick={() => savePrefs(local)}>{c.save}</button>
          <button type="button" className="cookie-btn primary" onClick={acceptAll}>{c.all}</button>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferences;
